import { useEffect, useState } from 'react';
import { BarChart3, Boxes, LayoutDashboard, LayoutGrid, Smartphone } from 'lucide-react';
import { NavLink, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { listApps } from '../../api/AppOtaApi';
import styles from './DeploymentWorkspaceNav.module.scss';

const sections = [
  { suffix: '', label: 'Overview', icon: LayoutDashboard, end: true },
  { suffix: '/channels', label: 'Channels & artifacts', icon: Boxes },
  { suffix: '/devices', label: 'Device assignments', icon: Smartphone },
  { suffix: '/statistics', label: 'Version adoption', icon: BarChart3 },
];

export const appBasePath = packageName => `/app-update/${encodeURIComponent(packageName)}`;

export const appLabel = app => app?.display_name || app?.package_name || '';

function DeploymentWorkspaceNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);

  const match = matchPath({ path: '/app-update/:packageName', end: false }, location.pathname);
  const packageName = match?.params.packageName ? decodeURIComponent(match.params.packageName) : '';
  const inWorkspace = location.pathname.startsWith('/app-update');

  useEffect(() => {
    if (!inWorkspace) {
      return undefined;
    }
    let active = true;
    listApps()
      .then(response => {
        if (active) {
          setApps(response.data || []);
        }
      })
      .catch(() => {
        if (active) {
          setApps([]);
        }
      });
    return () => {
      active = false;
    };
    // Re-fetch when the package changes so a freshly registered app shows up.
  }, [inWorkspace, packageName]);

  if (!inWorkspace) {
    return null;
  }

  const currentApp = apps.find(app => app.package_name === packageName);
  const sectionSuffix = packageName ? location.pathname.slice(appBasePath(packageName).length) : '';

  const switchApp = nextPackage => {
    if (!nextPackage) {
      navigate('/app-update');
      return;
    }
    navigate(`${appBasePath(nextPackage)}${sectionSuffix}`);
  };

  return (
    <section className={styles.workspaceNav} aria-label="App deployment workspace">
      <div className={styles.identity}>
        <span>Deployment workspace</span>
        {packageName ? (
          <label className={styles.appSwitcher}>
            <span className={styles.srOnly}>Switch application</span>
            <select value={packageName} onChange={event => switchApp(event.target.value)}>
              {!currentApp && <option value={packageName}>{packageName}</option>}
              {apps.map(app => (
                <option key={app.package_name} value={app.package_name}>
                  {appLabel(app)}
                </option>
              ))}
            </select>
            <small>{currentApp?.display_name ? packageName : 'Android package'}</small>
          </label>
        ) : (
          <strong>All applications</strong>
        )}
      </div>
      <nav className={styles.navigation} aria-label="App deployment sections">
        <NavLink
          to="/app-update"
          end
          className={({ isActive }) => `${styles.link} ${isActive ? styles.linkActive : ''}`}
        >
          <LayoutGrid size={16} />
          <span>Apps</span>
        </NavLink>
        {packageName &&
          sections.map(({ suffix, label, icon: Icon, end }) => (
            <NavLink
              key={suffix}
              to={`${appBasePath(packageName)}${suffix}`}
              end={end}
              className={({ isActive }) => `${styles.link} ${isActive ? styles.linkActive : ''}`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </NavLink>
          ))}
      </nav>
    </section>
  );
}

export default DeploymentWorkspaceNav;
